import React from 'react';
import { Tenant } from '../tenants';
import { ThemeConfig } from 'themes/themeConfig';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'constants/locale';
import { ThemeNames } from 'types/themes';
import ExprealtyCaTheme from 'themes/exprealtyCA';

export const EXP_CA_TENANT_ID = ThemeNames.EXP_REALTY_CA;
export const EXP_CA_L18N_NAMESPACE = 'expCA' as const;
export const EXP_CA_L18N_NAMESPACES = [EXP_CA_L18N_NAMESPACE, 'common'] as const;

const homeConfig = {
  internalLinks: [
    {
      title: 'Provinces & Territories',
      links: [
        {
          label: 'Alberta',
          link: '/alberta-real-estate',
        },
        {
          label: 'British Columbia',
          link: '/british-columbia-real-estate',
        },
        {
          label: 'Manitoba',
          link: '/manitoba-real-estate',
        },
        {
          label: 'New Brunswick',
          link: '/new-brunswick-real-estate',
        },
        {
          label: 'Newfoundland & Labrador',
          link: '/newfoundland-and-labrador-real-estate',
        },
        {
          label: 'Nova Scotia',
          link: '/nova-scotia-real-estate',
        },
        {
          label: 'Ontario',
          link: '/ontario-real-estate',
        },
        {
          label: 'Quebec',
          link: '/quebec-real-estate',
        },
        {
          label: 'Saskatchewan',
          link: '/saskatchewan-real-estate',
        },
        {
          label: 'Yukon',
          link: '/yukon-real-estate',
        },
      ],
    },
    {
      title: 'Metro Areas',
      links: [
        {
          label: 'Toronto',
          link: '/toronto-on-real-estate',
        },
        {
          label: 'Vancouver',
          link: '/vancouver-bc-real-estate',
        },
        {
          label: 'Calgary',
          link: '/calgary-ab-real-estate',
        },
        {
          label: 'Edmonton',
          link: '/edmonton-ab-real-estate',
        },
        {
          label: 'Ottawa',
          link: '/ottawa-on-real-estate',
        },
        {
          label: 'Hamilton',
          link: '/hamilton-on-real-estate',
        },
        {
          label: 'London',
          link: '/london-on-real-estate',
        },
        {
          label: 'Halifax',
          link: '/halifax-ns-real-estate',
        },
        {
          label: 'St. Catharines',
          link: 'st-catharines-on-real-estate',
        },
      ],
    },
    {
      title: 'Popular Ontario Cities',
      links: [
        {
          label: 'Barrie Homes for Sale',
          link: '/barrie-on-real-estate',
        },
        {
          label: 'Bradford West Homes for Sale',
          link: '/bradford-west-gwillimbury-on-real-estate',
        },
        {
          label: 'Cambridge Homes for Sale',
          link: '/cambridge-on-real-estate',
        },
        {
          label: 'Collingwood Homes for Sale',
          link: '/collingwood-on-real-estate',
        },
        {
          label: 'Guelph Homes for Sale',
          link: '/guelph-on-real-estate',
        },
        {
          label: 'Hamilton Homes for Sale',
          link: '/hamilton-on-real-estate',
        },
        {
          label: 'Kitchener Homes for Sale',
          link: '/kitchener-on-real-estate',
        },
        {
          label: 'London Homes for Sale',
          link: '/london-on-real-estate',
        },
        {
          label: 'Orillia Homes for Sale',
          link: '/orillia-on-real-estate',
        },
        {
          label: 'Ottawa Homes for Sale',
          link: '/ottawa-on-real-estate',
        },
        {
          label: 'Peterborough Homes for Sale',
          link: '/peterborough-on-real-estate',
        },
        {
          label: 'Waterloo Homes for Sale',
          link: '/waterloo-on-real-estate',
        },
      ],
    },
    {
      title: 'Popular Western Canada Cities',
      links: [
        {
          label: 'Vancouver Homes for Sale',
          link: '/vancouver-bc-real-estate',
        },
        {
          label: 'Burnaby Homes for Sale',
          link: '/burnaby-bc-real-estate',
        },
        {
          label: 'Coquitlam Homes for Sale',
          link: '/coquitlam-bc-real-estate',
        },
        {
          label: 'Maple Ridge Homes for Sale',
          link: '/maple-ridge-bc-real-estate',
        },
        {
          label: 'New Westminster Homes for Sale',
          link: '/new-westminster-bc-real-estate',
        },
        {
          label: 'North Vancouver Homes for Sale',
          link: '/north-vancouver-bc-real-estate',
        },
        {
          label: 'Richmond Homes for Sale',
          link: '/richmond-bc-real-estate',
        },
        {
          label: 'Surrey Homes for Sale',
          link: '/surrey-bc-real-estate',
        },
        {
          label: 'Victoria Homes for Sale',
          link: '/victoria-bc-real-estate',
        },
        {
          label: 'White Rock Homes for Sale',
          link: '/white-rock-bc-real-estate',
        },
        {
          label: 'Calgary Homes for Sale',
          link: '/calgary-ab-real-estate',
        },
        {
          label: 'Edmonton Homes for Sale',
          link: '/edmonton-ab-real-estate',
        },
      ],
    },
  ],
};

export const joinExpCAConfig = {
  joinFormLink: 'https://join.exprealty.com/contact-ca/',
  glennQuoteConfig: {
    authorImage: '/next/assets/images/join-expus/Glenn.jpg',
    authorImageAlt: 'Glenn Sanford',
    authorTitle: 'FOUNDER',
    authorName: 'Glenn Sanford',
    quoteContent: React.createElement(React.Fragment, null,
      'In founding eXp Realty, my goal was clear: redefine real estate with ',
      React.createElement('span', { className: 'highlight' }, 'unmatched'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'compensation'),
      ' and ',
      React.createElement('span', { className: 'highlight' }, 'equity'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'opportunities'),
      ' for agents. We go beyond financials, offering a supportive ecosystem with cutting-edge technology, comprehensive training, and strong support, empowering agents to lead.'
    ),
  },
  leoQuoteConfig: {
    authorImage: '/next/assets/images/join-expus/Leo.jpg',
    authorImageAlt: 'Leo Pareja',
    authorTitle: 'eXp CEO',
    authorName: 'Leo Pareja',
    quoteContent: React.createElement(React.Fragment, null,
      'At eXp Realty, we\'re not just redefining the real estate industry — we\'re empowering agents to achieve their ',
      React.createElement('span', { className: 'highlight' }, 'full'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'potential'),
      '. With ',
      React.createElement('span', { className: 'highlight' }, 'cutting-edge'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'technology,'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'unparalleled'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'support,'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'and'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'a'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'model'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'that'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'puts'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'agents'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'first,'),
      ' we\'re creating opportunities that simply don\'t exist anywhere else. If you\'re ready to ',
      React.createElement('span', { className: 'highlight' }, 'take'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'your'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'career'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'to'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'the'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'next'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'level,'),
      ' this is ',
      React.createElement('span', { className: 'highlight' }, 'where'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'the'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'pros'),
      ' ',
      React.createElement('span', { className: 'highlight' }, 'grow.')
    ),
  },
  videoCardData: [
    {
      videoId: 'hv5oA6q_phM',
      bottomText: 'A model with no limits',
    },
    {
      videoId: 'AA76L8hw7oU',
      bottomText: '"Fresh and exciting adventure in real estate"',
    },
    {
      videoId: 'pGH14z9XpRc',
      bottomText: 'Where top-producing teams find their future',
    },
    {
      videoId: 'vK24zkG4C0k',
      bottomText: '"eXp isn\'t just where I\'ll do business—it\'s where I\'ll be growing alongside the best in the industry."',
    },
    {
      videoId: 'VdulH47weDY',
      bottomText: 'A new chapter begins.',
    },
    {
      videoId: 'H_DP7Pmme0s',
      bottomText: '"Where everything feels just right"',
    },
    {
      videoId: 'xcx9cySzqFY',
      bottomText: 'From MMA champion to real estate leader',
    },
    {
      videoId: 'x0dZCrsGBAw',
      bottomText: '"Aligning values with purpose, building a life that matters"',
    },
  ],
  cardStackData: [
    {
      title: 'Freedom to Build Your Future',
      description: 'At eXp, you\'re not bound by borders or office walls. Our virtual model gives you the power to choose where, how, and when you build your business. Unlock unlimited earning potential and create the lifestyle you\'ve always envisioned — on your terms.',
      imageSrc: '/next/assets/images/join-expus/card-stack-image-1.png',
      backgroundColor: '#0C0F24',
      textColor: '#fff',
      imageAlt: 'A woman smiling and dancing with a phone in her hand.',
    },
    {
      title: 'Ownership That Pays You Back',
      description: 'You don\'t just work at eXp — you own a piece of it. From equity awards to revenue share, our model rewards agents for growth, not just sales. Build wealth while you build your business.',
      imageSrc: '/next/assets/images/join-expus/card-stack-image-2.png',
      backgroundColor: '#6D6F7C',
      textColor: '#fff',
      imageAlt: 'Two men sitting down at a desk with laptops while a third man is standing behind them.',
    },
    {
      title: 'Growth Without Limits',
      description: 'Whether you\'re brand-new or a seasoned pro, eXp equips you with top-tier training, live masterminds, and on-demand coaching. With 80K+ agents worldwide and cutting-edge tech at your fingertips, there\'s no ceiling on your success.',
      imageSrc: '/next/assets/images/join-expus/card-stack-image-3.png',
      backgroundColor: '#DAE0E7',
      textColor: '#0C0F24',
      imageAlt: 'A man looking up while recording with his phone.',
    },
    {
      title: 'Community That Has Your Back',
      description: 'You\'re never building alone. At eXp, you\'re part of a global network of like-minded entrepreneurs who support, mentor, and celebrate each other. From day one, you\'re welcomed into a culture where collaboration beats competition.',
      imageSrc: '/next/assets/images/join-expus/card-stack-image-4.png',
      backgroundColor: '#EDF2F7',
      textColor: '#0C0F24',
      imageAlt: 'Five people smiling preparing to take a selfie.',
    },
  ],
  programsData: [
    {
      image: '/next/assets/images/join-expus/program-3.jpeg',
      title: 'Boost',
      description: "Discover our Boost Incentive for teams needing extra support in admin, tech, and more. We're eager to understand your team's culture and performance.",
      buttonText: 'Are you a Boost Candidate?',
      buttonLink: 'https://docs.google.com/forms/d/e/1FAIpQLScb_rXcHYN2yOY7VFAuG1UhvjTwpKRWteFsB_fZhnzsnBZTZg/viewform',
    },
    {
      image: '/next/assets/images/join-expus/program-4.jpeg',
      title: 'Thrive',
      description: 'Supports team operations, easing the transition to eXp Realty for culturally-aligned teams. Interested in integrating with our community? Let\'s discuss your team\'s future.',
      buttonText: 'Are you a Thrive Candidate?',
      buttonLink: 'https://docs.google.com/forms/d/e/1FAIpQLSeIE4Y1gVhUACFa1DEj_qD0k-1zqWBKn8s6X3a1fCDpvXJodQ/viewform',
    },
  ],
  faqData: [
    {
      title: 'Where is eXp Realty open for business?',
      children: React.createElement('p', null, 
        'eXp has a global presence in over 24 countries around the world. To see all of our locations, ',
        React.createElement('a', { href: 'https://exprealty.international/', target: '_blank' }, 'click here.')
      ),
    },
    {
      title: 'Do I need to be a licensed real estate agent to join eXp Realty?',
      children: React.createElement('p', null, 'Yes, an active real estate license is required to join eXp Realty as an agent.'),
    },
    {
      title: 'What are the fees to join?',
      children: React.createElement(React.Fragment, null,
        React.createElement('p', { style: { fontWeight: 'bold', marginBottom: '0.5rem' }}, 'Canada agent fees (CDN):'),
        React.createElement('ul', null,
          React.createElement('li', null, '$199 start-up fee'),
          React.createElement('li', null, '$139/month cloud brokerage fee')
        ),
        React.createElement('p', { style: { fontWeight: 'bold', marginTop: '1rem', marginBottom: '0.5rem' }}, 'Canada agent transaction fees (CDN):'),
        React.createElement('ul', null,
          React.createElement('li', null, '$29/transaction broker review fee'),
          React.createElement('li', null, '$17/transaction risk management fee ($200 annual cap)')
        ),
        React.createElement('p', { style: { fontWeight: 'bold', marginTop: '1rem', marginBottom: '0.5rem' }}, 'U.S. agent fees (USD):'),
        React.createElement('ul', null,
          React.createElement('li', null, '$149 start-up fee'),
          React.createElement('li', null, '$85/month cloud brokerage fee')
        ),
        React.createElement('p', { style: { fontWeight: 'bold', marginTop: '1rem', marginBottom: '0.5rem' }}, 'U.S. agent transaction fees (USD):'),
        React.createElement('ul', null,
          React.createElement('li', null, '$25/transaction broker review fee'),
          React.createElement('li', null, '$60/transaction risk management fee ($750 annual cap)')
        )
      ),
    },
    {
      title: 'Is eXp Realty a good fit for new agents?',
      children: React.createElement('p', null,
        'eXp provides a learning platform to newly licensed agents. The eXpand Mentoring Program provides newly licensed agents a learning platform where they will gain the fundamental knowledge required to start and build a successful real estate career. Each mentee is paired with a certified eXp mentor who is a member of the same local geographic area.'
      ),
    },
    {
      title: 'Can my team or brokerage join?',
      children: React.createElement('p', null,
        'Individual agents, teams and independent brokerages of all sizes are joining eXp.'
      ),
    },
    {
      title: 'What is the eXp Realty compensation plan?',
      children: React.createElement(React.Fragment, null,
        React.createElement('p', null,
          'eXp Realty offers competitive commission splits, stock awards, and revenue share. Learn more about our compensation plan here.'
        ),
        React.createElement('ul', null,
          React.createElement('li', null, 'Canada: ',
            React.createElement('a', { href: 'https://www.exprealty.ca/income#revenue-share', target: '_blank', rel: 'noopener' }, 'https://www.exprealty.ca/income#revenue-share')
          ),
          React.createElement('li', null, 'US: ',
            React.createElement('a', { href: 'https://www.exprealty.com/income', target: '_blank', rel: 'noopener' }, 'https://www.exprealty.com/income')
          )
        )
      ),
    },
    {
      title: 'Who can I contact to learn more?',
      children: React.createElement('p', null,
        'Talk to your sponsor or reach out to an eXp agent in your area. Or, use our ',
        React.createElement('a', { href: 'https://join.exprealty.com/contact-ca/', target: '_blank', rel: 'noopener' }, 'contact form'),
        ' to get in touch.'
      ),
    },
  ],
};

const expCATenant: Tenant<ThemeConfig> = {
  id: EXP_CA_TENANT_ID,
  theme: ExprealtyCaTheme,
  localization: {
    languages: [SiteLanguage.English],
    countries: [CountryCodeList.CANADA],
    namespaces: EXP_CA_L18N_NAMESPACES,
  },
  config: {
    home: homeConfig,
    joinExp: joinExpCAConfig,
  },
  debug: {
    forceTenantHeaderValue: 'useTenant="useExpCA"',
  },
};

export default expCATenant;
